import React, { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import qs from 'query-string';

import {
    MainContainer,
    MainTitle,
    TitleContainer,
    Paragraph,
    ButtonsRow,
    MainButton,
    ColumnContainer,
    Column,
    Divider,
    SectionTitle,
    LoadingContainer,
    HeadSeo,
    Breadcrumbs,
    GoogleAnalytics,
    CardContainer,
    SimpleLink,
    NormalPopup,
    ClosedBookingPlaceholder,
    TypeButtons
} from "@beacharound/beacharound-ui";

import { formatDate, getAddressString } from "@beacharound/beacharound-ui-helpers";

import { getPlaceholderImage } from '../../helpers/images/getPlaceholderImage';
import { getGeoBreadCrumbs } from '../../helpers/getGeoBreadCrumbs';

import { getUser } from '../../api/user';
import { createView } from '../../api/views';
import { getBeach } from '../../api/beach';
import { getReviews, getReviewsCounter } from '../../api/reviews';
import { getEvents } from '../../api/events';
import { getProducts } from '../../api/products';

import AccessibilitiesList from '../../components/Accessibilities/AccessibilitiesList';
import BeachesEventsSlider from '../../components/Beaches/BeachesEventsSlider';
import BeachesPhonePopup from '../../components/Beaches/BeachesPhonePopup';
import BeachesProductsAvailabilitiSlider from '../../components/Beaches/BeachesProductsAvailabilitiSlider';
import BeachesProductsSeasonalSlider from '../../components/Beaches/BeachesProductsSeasonalSlider';
import BeachesProductsSlider from '../../components/Beaches/BeachesProductsSlider';
import BeachesPromotionsHighlight from '../../components/Beaches/BeachesPromotionsHighlight';
import BookingCalendar from '../../components/Beaches/Booking/BookingCalendar';
import GalleryComponent from '../../components/Gallery/GalleryComponent';
import HotelsSlider from '../../components/Hotels/HotelsSlider';
import NotFoundRedirect from '../../components/NotFound/NotFoundRedirect';
import PlacesInfoAddress from '../../components/PlacesInfo/PlacesInfoAddress';
import ReviewsPlaceCount from '../../components/Reviews/ReviewsPlaceCount';
import ReviewsPlaceList from '../../components/Reviews/ReviewsPlaceList';
import RulesList from '../../components/Rules/RulesList';
import ServicesList from '../../components/Services/ServicesList';
import SignUserComponent from '../../components/User/SignUserComponent';
import BeachesRentalsSlider from '../../components/Beaches/BeachesRentalsSlider';
import { AdSense } from '../../components/AdSense/AdSense';
import PrerenderMeta from '../../components/Prerender/PrerenderMeta';


export default function BeachDetail({ match, history }) {

    const { search } = useLocation();
    const { externalWebsiteUrl, externalSocialLink, widget } = qs.parse(search)
    const [callPopup, setCallPopup] = useState()
    const { beachSlug } = match.params;
    const [signPopup, setSignPopup] = useState()
    const [searching, setSearching] = useState();

    const { data: user } = useQuery("user", getUser);

    const { data: beach, isLoading, error } = useQuery(["beaches", beachSlug], () =>
        getBeach({ slug: beachSlug }),
        { enabled: !!beachSlug }
    );

    const eventsFromDate = formatDate(new Date(), 'yyyy-MM-dd');
    const eventsParams = {
        fromDate: eventsFromDate,
        params: { typeOfAccess: "LIMITED" },
        sort: { date: 1 },
        limit: 1
    };
    const { data: events } = useQuery(["events", beachSlug, eventsParams], () =>
        getEvents({ activitySlug: beachSlug, params: eventsParams }),
        {
            enabled: !!beachSlug,
            refetchOnWindowFocus: false
        }
    );

    const rentalsParams = {
        activitySlug: beachSlug,
        limit: 5,
        params: {
            bundleType: { $exists: false },
            type: "rental",
        }
    };

    const { data: rentals } = useQuery(['products', beachSlug, 'rental', rentalsParams], () =>
        getProducts(rentalsParams), {
        enabled: !!beachSlug,
        refetchOnWindowFocus: false
    }
    );

    const reviewsOptions = {
        limit: 10,
        sort: { createdAt: -1 },
        reviewed: beach?._id,
    };

    const { data: reviews } = useQuery(["reviews", reviewsOptions], () =>
        getReviews({ params: reviewsOptions }),
        { enabled: !!beach?._id }
    );

    const { data: reviewsCount } = useQuery(["reviews", "counter", beach?._id], () =>
        getReviewsCounter({ params: { reviewed: beach?._id } }),
        { enabled: !!beach?._id }
    );

    const { mutate, status } = useMutation(createView, {
        onSuccess: (data) => {
            if (!user) {
                return setSignPopup(true)
            }
            return setCallPopup(true)
        }
    });

    const beachName = beach?.name || 'Spiaggia';
    const cityName = beach?.address?.city;
    const beachAddress = beach?.address;

    let seoAdditions = beach && beachAddress && {
        "name": beach.name,
        "url": match.url,
        "free": beach?.type === "public",
        "tourBookingPage": `${window.location.origin}/it/prenotazione/spiaggia/${beach.slug}`,
        "address": {
            "addressLocality": beachAddress.city,
            "addressRegion": "Italy",
            "postalCode": beachAddress.postalCode,
            "streetAddress": `${beachAddress.street} ${beachAddress.streetNumber}`,
            "addressCountry": "IT"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": beachAddress.geocode?.coordinates?.[1],
            "longitude": beachAddress.geocode?.coordinates?.[0]
        }
    }

    if (!!beach?.services?.length) {
        seoAdditions = {
            ...seoAdditions,
            "amenityFeature": beach.services?.map((serv) => {
                return {
                    "@type": "LocationFeatureSpecification",
                    "value": "True",
                    "name": serv.service?.name,
                    "url": `${window.location.origin}/it/prenota-un-ombrellone-nelle-migliori-spiagge-${serv.service?.name.replace(' ', '-')}`
                }
            }),
        }
    }

    if (!!reviewsCount?.count) {
        seoAdditions = {
            ...seoAdditions,
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingCount": reviewsCount?.count,
                "bestRating": "5",
                "worstRating": "1",
                "ratingValue": reviewsCount?.median
            }
        }
    }

    if (!!reviews?.length) {
        seoAdditions = {
            ...seoAdditions,
            "review": reviews?.map(review => ({
                "@type": "Review",
                "author": review.user ? [review.user.profile?.firstName, review.user.profile?.lastName].filter(Boolean).join(" ") : "Anonimo",
                "datePublished": formatDate(new Date(review.createdAt), "yyyy-MM-dd"),
                "reviewBody": review.description,
                "name": review.title,
                "reviewRating": {
                    "@type": "Rating",
                    "bestRating": "5",
                    "ratingValue": review.vote,
                    "worstRating": "1"
                }
            }))
        }
    }

    async function callAndBook() {
        await mutate({
            type: 'phone',
            beachSlug: beach?.slug
        })
    }

    let otherBookings = [];

    if (!!events?.length && !!beach?.slugCity) {

        let eventsLink = `/prenotazione-eventi/${beach?.slugCity}`;

        if (!!externalSocialLink) {
            eventsLink = `/prenotazione-eventi/${beach?.slugCity}?${qs.stringify({ externalSocialLink })}`;
        }
        if (!!externalWebsiteUrl) {
            eventsLink = `/prenotazione-eventi/${beach?.slugCity}?${qs.stringify({ externalWebsiteUrl })}`;
        }

        otherBookings = [
            ...otherBookings,
            {
                fa: true,
                icon: "champagne-glasses",
                text: "Prenota l'ingresso ad un evento",
                link: eventsLink
            }
        ];
    }
    if (!!rentals?.length && !!beach?.slugCity) {

        let servicesLink = `/prenotazione-servizi/${beach?.slugCity}`;

        if (!!externalSocialLink) {
            servicesLink = `/prenotazione-servizi/${beach?.slugCity}?${qs.stringify({ externalSocialLink })}`;
        }
        if (!!externalWebsiteUrl) {
            servicesLink = `/prenotazione-servizi/${beach?.slugCity}?${qs.stringify({ externalWebsiteUrl })}`;
        }

        otherBookings = [
            ...otherBookings,
            {
                fa: true,
                icon: "table-tennis-paddle-ball",
                text: "Prenota un'esperienza unica",
                link: servicesLink
            }
        ];
    }

    return (
        <MainContainer>
            <NotFoundRedirect error={error} url="/spiagge" />

            <LoadingContainer isLoading={isLoading}>
                {beach && (
                    <Fragment>
                        <HeadSeo
                            title={beach?.type === 'private' ? [`Prenota il tuo ombrellone a ${beachName}`] : [`Prenota il tuo posto a ${beachName} ${cityName}`]}
                            name={beachName}
                            descriptionFull={beach.description}
                            description={`Prenota il tuo ombrellone a ${beachName}. Scegli tra sdraio o lettino nel tuo stabilimento balneare ed al tuo posto pensiamo noi`}
                            type={beach?.type === "private" ? "LocalBusiness" : "Beach"}
                            image={beach.cover || getPlaceholderImage("beaches", beach)}
                            additions={seoAdditions}
                        />
                        <PrerenderMeta isLoading={isLoading} />

                        <GoogleAnalytics name={beachName} type="beach" />

                        {widget === "true" ?
                            beach?.bookable && beach?.type === 'private' ?
                                <LoadingContainer isLoading={isLoading}>
                                    <BookingCalendar
                                        beach={beach}
                                        setSearching={setSearching}
                                    />
                                </LoadingContainer>
                                : (
                                    <ClosedBookingPlaceholder
                                        activity={beach}
                                        logoSrc={`${process.env.REACT_APP_IMAGES_PATH_ROOT}/versions/${process.env.REACT_APP_STYLE_MAIN_VERSION}/logo-w.png`}
                                    />
                                )
                            : (
                                <Fragment>
                                    <TitleContainer>
                                        {(!externalWebsiteUrl && !externalSocialLink) &&
                                            <Breadcrumbs
                                                breadcrumbs={beach?.geoElements && getGeoBreadCrumbs(beach?.geoElements, {
                                                    beaches: true, others: [{
                                                        label: beachName,
                                                        url: match.url
                                                    }]
                                                })}
                                                linkComponent={Link}
                                            />
                                        }
                                        <MainTitle
                                            text={`Prenota il tuo ombrellone a <strong>${beachName}</strong> a ${cityName}`}
                                            component="h1"
                                        />
                                        <ReviewsPlaceCount place={beach?._id} />
                                        <Paragraph text={getAddressString(beach?.address)} />
                                    </TitleContainer>

                                    {!beach.hasOwner && (!externalWebsiteUrl && !externalSocialLink) && (
                                        <ColumnContainer lowMargin>
                                            <Column>
                                                {/* <!-- Banner orizzontali --> */}
                                                <AdSense
                                                    client="ca-pub-7021525903157736"
                                                    slot="5696468559"
                                                />

                                                <CardContainer>
                                                    <Paragraph
                                                        text="Il contenuto di questa pagina è frutto di ricerche e materiale raccolto dagli utenti.<br/> <strong>Sei il proprietario di questa spiaggia?</strong>"
                                                    />
                                                    <SimpleLink
                                                        text="Gestisci questa spiaggia"
                                                        component="a"
                                                        href={`${process.env.REACT_APP_BUSINESS_URL}/user/signup?activityId=${beach?._id}`}
                                                        target="_blank"
                                                    />
                                                </CardContainer>
                                            </Column>
                                        </ColumnContainer>
                                    )}

                                    {(beach?.cover || beach?.gallery?.length > 0) &&
                                        <ColumnContainer noMargin>
                                            <Column>
                                                <GalleryComponent
                                                    cover={beach.cover}
                                                    gallery={beach.gallery}
                                                    name={beachName}
                                                />
                                            </Column>
                                        </ColumnContainer>
                                    }

                                    {beach?.bookable && beach?.type === 'private' &&
                                        <Fragment>
                                            {/* {!externalWebsiteUrl && !externalSocialLink && beach.hasMenu &&
                                                <MainButton
                                                    color="blue"
                                                    text="Ordina da mangiare"
                                                    component={Link}
                                                    to={`/ordine-in-spiaggia/${beach.slugCity}/ombrellone`}
                                                />
                                            } */}
                                            {!!otherBookings.length &&
                                                <ColumnContainer>
                                                    <Column>
                                                        <TypeButtons
                                                            component={Link}
                                                            buttons={otherBookings}
                                                            small
                                                        />
                                                    </Column>
                                                </ColumnContainer>
                                            }
                                            <BeachesPromotionsHighlight
                                                match={match}
                                            />
                                            <ColumnContainer noMargin>
                                                <Column>
                                                    <LoadingContainer isLoading={isLoading || searching} loadingText={searching && 'Stiamo cercando i posti disponibili...'}>
                                                        {beach?.bookingType === "MANUAL" ? (
                                                            <TitleContainer center>
                                                                <SectionTitle center text="Fai la tua richiesta di prenotazione!" />
                                                                <Paragraph center text={`
                                                                    Questa spiaggia prevede la conferma da parte del gestore. <br/>
                                                                    Completa la tua richiesta di prenotazione <br/>
                                                                    e attendi la risposta della spiaggia!
                                                                `} />
                                                            </TitleContainer>
                                                        ) : (
                                                            <TitleContainer center>
                                                                <SectionTitle center text="Prenota ora il tuo posto in spiaggia!" />
                                                                <Paragraph center text="Seleziona le date di check-in e check-out e scegli il tuo ombrellone preferito." />
                                                            </TitleContainer>
                                                        )}
                                                        <BookingCalendar
                                                            beach={beach}
                                                            setSearching={setSearching}
                                                        />
                                                        <Divider />
                                                    </LoadingContainer>
                                                </Column>
                                            </ColumnContainer>
                                        </Fragment>
                                    }

                                    {((!beach.bookable && beach?.type === 'private' && beach?.hasPhone) || (beach.bookable && beach?.type === 'public') || (!beach.bookable && !externalWebsiteUrl && !externalSocialLink)) &&
                                        <ColumnContainer lowMargin>
                                            <Column>
                                                <ButtonsRow fill>
                                                    {!beach.bookable && beach?.type === 'private' && beach?.hasPhone &&
                                                        <MainButton
                                                            text="Chiama e prenota"
                                                            border
                                                            action={() => callAndBook()}
                                                            status={status}
                                                        />
                                                    }
                                                    {beach?.bookable && beach?.type === 'public' && (
                                                        <MainButton
                                                            text="Richiedi accesso"
                                                            component={Link}
                                                            to={`/prenotazione/spiaggia-libera/${beach?.slug}/tipologia`}
                                                        />
                                                    )}
                                                    {!beach.bookable && !externalWebsiteUrl && !externalSocialLink &&
                                                        <MainButton
                                                            border
                                                            text="Racconta la tua esperienza!"
                                                            component={Link}
                                                            to={`${match.url}/recensioni/nuova`}
                                                        />
                                                    }
                                                </ButtonsRow>
                                            </Column>
                                        </ColumnContainer>
                                    }

                                    {(beach?.subtitle || beach?.description) && (
                                        <ColumnContainer noMargin>
                                            <Column>
                                                <TitleContainer>
                                                    <SectionTitle text={beach.subtitle || `Informazioni sulla spiaggia`} component="div" />
                                                    <Paragraph text={beach.description} />
                                                </TitleContainer>
                                            </Column>
                                        </ColumnContainer>
                                    )}

                                    <ColumnContainer lowMargin>
                                        <Column>
                                            <Divider noMargin />
                                            <PlacesInfoAddress address={beach?.address} />
                                            <Divider noMargin />
                                        </Column>
                                    </ColumnContainer>

                                    <ColumnContainer noMargin>
                                        <Column>
                                            {/* <!-- Banner orizzontali --> */}
                                            <AdSense
                                                client="ca-pub-7021525903157736"
                                                slot="6366051385"
                                            />
                                        </Column>
                                    </ColumnContainer>

                                    {beach?.type !== "public" &&
                                        <BeachesProductsAvailabilitiSlider match={match} hasMap={beach?.hasMap} isBookable={beach?.bookable} />
                                    }

                                    {beach?.type !== "public" && (!externalWebsiteUrl && !externalSocialLink) &&
                                        <BeachesProductsSlider match={match} />
                                    }

                                    {beach?.type !== "public" && (!externalWebsiteUrl && !externalSocialLink) &&
                                        <BeachesProductsSeasonalSlider match={match} />
                                    }

                                    {beach?.type !== "public" && (!externalWebsiteUrl && !externalSocialLink) &&
                                        <BeachesEventsSlider match={match} />
                                    }

                                    {beach?.type !== "public" && (!externalWebsiteUrl && !externalSocialLink) &&
                                        <BeachesRentalsSlider match={match} />
                                    }

                                    {beach?.services?.length > 0 && (
                                        <ServicesList services={beach.services} />
                                    )}

                                    {beach?.accessibilities?.length > 0 && (
                                        <AccessibilitiesList accessibilities={beach.accessibilities} />
                                    )}

                                    {beach?.rules?.length > 0 && (
                                        <RulesList rules={beach.rules} />
                                    )}

                                    <ColumnContainer noMargin>
                                        <Column>
                                            {/* <!-- Banner orizzontali --> */}
                                            <AdSense
                                                client="ca-pub-7021525903157736"
                                                slot="7679133056"
                                            />
                                        </Column>
                                    </ColumnContainer>

                                    {(!externalWebsiteUrl && !externalSocialLink) &&
                                        <ReviewsPlaceList place={beach?._id} beachImage={beach?.cover} />
                                    }

                                    {(!externalWebsiteUrl && !externalSocialLink) &&
                                        <HotelsSlider
                                            title={`I migliori hotel vicino a ${beachName}`}
                                            subtitle={`Scopri i migliori hotel di ${beach?.address?.city} vicino a ${beachName} e prenota la camera giusta per il tuo soggiorno!`}
                                            place={beach?.slug}
                                        />
                                    }

                                    {!beach.hasOwner && (!externalWebsiteUrl && !externalSocialLink) && (
                                        <ColumnContainer>
                                            <Column>
                                                <CardContainer dark>
                                                    <TitleContainer>
                                                        <SectionTitle center color="white" text="Sei il gestore di questa spiaggia?" />
                                                        <Paragraph center color="white" text="<strong>Registrati gratuitamente</strong> per tenere aggiornate le sue informazioni, caricare le foto, rispondere alle recensioni e tanto altro ancora!" component="div" />
                                                    </TitleContainer>

                                                    <ButtonsRow center>
                                                        <MainButton
                                                            color="white"
                                                            text="Prendila in carico!"
                                                            component="a"
                                                            to={`${process.env.REACT_APP_BUSINESS_URL}/user/signup?activityId=${beach?._id}`}
                                                            blank
                                                        />
                                                    </ButtonsRow>
                                                </CardContainer>
                                            </Column>
                                        </ColumnContainer>
                                    )}

                                    {(!externalWebsiteUrl && !externalSocialLink) &&
                                        <ColumnContainer noMargin>
                                            <Column>
                                                <TitleContainer>
                                                    <Paragraph
                                                        text={`Prenota il tuo ombrellone, sdraio, lettino a ${cityName} nello stabilimento balneare di ${beachName}.`}
                                                        component="h2"
                                                    />
                                                </TitleContainer>
                                            </Column>
                                        </ColumnContainer>
                                    }
                                </Fragment>
                            )}
                    </Fragment>
                )}
            </LoadingContainer>

            <BeachesPhonePopup
                visible={callPopup}
                setVisible={setCallPopup}
                beach={beach}
            />

            <NormalPopup
                visible={signPopup}
                setVisible={setSignPopup}
                title="Accedi per chiamare"
            >
                <SignUserComponent
                    noMargin
                    match={match}
                    loginCallback={() => {
                        setSignPopup();
                        return setCallPopup(true)
                    }}
                    signupCallback={() => {
                        setSignPopup();
                        return setCallPopup(true)
                    }}
                />
            </NormalPopup>
        </MainContainer>
    );
}